import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Input, Select } from "./ui";
import SearchableSelect from "./SearchableSelect";
import VendorSelect from "./VendorSelect";
import NumberInput from "./NumberInput";
import { fmtIDR } from "../lib/utils";
import { Plus, Trash2, Copy } from "lucide-react";

const todayStr = () => new Date().toISOString().slice(0, 10);
export const STATUS_STYLE = {
  plan: "bg-sky-100 text-sky-800",
  settled: "bg-emerald-100 text-emerald-800",
  waiting: "bg-amber-100 text-amber-800",
};
export const newItem = (target = "awb") => ({
  _k: Math.random().toString(36).slice(2), target, awb_id: "", amount: "", description: "", transaction_date: todayStr(),
  expense_category: "", vendor_name: "", unpaid_amount: "", hutang_ref: "", status: "plan",
});

export const fromServer = (items = []) => items.map((i) => ({
  ...newItem(i.target), ...i, _k: i.item_id, amount: String(i.amount ?? ""), unpaid_amount: i.unpaid_amount ? String(i.unpaid_amount) : "",
  awb_id: i.awb_id || "", vendor_name: i.vendor_name || "", expense_category: i.expense_category || "", description: i.description || "",
  transaction_date: i.transaction_date || "", hutang_ref: i.cost_id ? `cost:${i.cost_id}` : i.plan_item_id ? `plan:${i.plan_panjar_id}:${i.plan_item_id}` : "",
}));

export const toPayload = (items) => items.filter((i) => parseFloat(i.amount) > 0).map((i) => {
  const [kind, a, b] = (i.hutang_ref || "").split(":");
  return {
    item_id: i.item_id || null, target: i.target, amount: parseFloat(i.amount), description: i.description || null,
    transaction_date: i.transaction_date || null,
    awb_id: i.target === "awb" ? i.awb_id || null : null,
    vendor_name: i.target === "awb" ? i.vendor_name || null : null,
    unpaid_amount: i.target === "awb" ? parseFloat(i.unpaid_amount) || 0 : 0,
    expense_category: i.target === "finance" ? i.expense_category || null : null,
    cost_id: i.target === "hutang" && kind === "cost" ? a : null,
    plan_panjar_id: i.target === "hutang" && kind === "plan" ? a : null,
    plan_item_id: i.target === "hutang" && kind === "plan" ? b : null,
  };
});

export const presettleTotals = (items) => ({
  paid: items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0),
  unpaid: items.reduce((s, i) => s + (i.target === "awb" ? parseFloat(i.unpaid_amount) || 0 : 0), 0),
});

// Opsi hutang: hutang vendor terbuka + item DP yang masih Rencana di panjar lain
export function useHutangOptions(excludePanjarId) {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    Promise.all([
      api.get("/costs", { params: { payment_type: "Accrue", is_settled: false } }).catch(() => ({ data: [] })),
      api.get("/cash-advances-dp-plans").catch(() => ({ data: [] })),
    ]).then(([c, p]) => {
      const costs = (c.data || []).filter((x) => x.cost_category === "Vendor").map((x) => ({
        value: `cost:${x.id}`, label: `${x.vendor_name || "-"} · ${x.awb_number || ""}`,
        sub: `Hutang · sisa ${fmtIDR(Math.max(0, (x.amount || 0) - (x.paid_amount || 0)))}${x.source_number ? " · DP " + x.source_number : ""}`,
      }));
      const plans = (p.data || []).filter((x) => x.panjar_id !== excludePanjarId).map((x) => ({
        value: `plan:${x.panjar_id}:${x.item_id}`, label: `${x.vendor_name} · ${x.awb_number}`,
        sub: `Rencana DP ${x.panjar_number} (belum settle) · sisa ${fmtIDR(x.unpaid_amount)}`,
      }));
      setOpts([...plans, ...costs]);
    });
  }, [excludePanjarId]);
  return opts;
}

const TARGET_LABEL = { awb: "AWB", operation: "Operation", finance: "Finance", hutang: "Pelunasan Hutang" };

function ItemRow({ it, i, upd, remove, dup, orders, expenseCats, vendors, loadVendors, hutangOptions }) {
  const locked = it.status === "settled";
  const showDp = it.target === "awb" && (it._dp || !!it.vendor_name || parseFloat(it.unpaid_amount) > 0);
  return (
    <div className={`rounded border p-2 space-y-1.5 ${locked ? "bg-emerald-50/50 border-emerald-200" : "border-slate-200"}`} data-testid={`presettle-row-${i}`}>
      <div className="grid grid-cols-12 gap-2 items-center">
        <div className="col-span-2">
          <Select data-testid={`presettle-target-${i}`} disabled={locked} value={it.target} onChange={(e) => upd(i, { target: e.target.value })}>
            {Object.entries(TARGET_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Select>
        </div>
        <div className="col-span-3">
          {it.target === "awb" && (locked ? <div className="text-sm font-mono px-2">{it.awb_number}</div> : (
            <SearchableSelect testid={`presettle-awb-${i}`} value={it.awb_id} onChange={(v) => upd(i, { awb_id: v })}
              options={[...(it.awb_id && !orders.some((o) => o.id === it.awb_id) ? [{ value: it.awb_id, label: it.awb_number || "AWB terpilih" }] : []),
                ...orders.map((o) => ({ value: o.id, label: o.awb_number, sub: `${o.status} · ${o.customer_name} · ${o.origin}→${o.destination}` }))]}
              placeholder="— Pilih AWB —" emptyText="AWB tidak ditemukan"/>
          ))}
          {it.target === "finance" && (
            <Select data-testid={`presettle-cat-${i}`} disabled={locked} value={it.expense_category} onChange={(e) => upd(i, { expense_category: e.target.value })}>
              <option value="">— Pilih kategori —</option>
              {expenseCats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </Select>
          )}
          {it.target === "operation" && <div className="text-xs text-emerald-700 px-2">HPP langsung (tanpa AWB)</div>}
          {it.target === "hutang" && (locked ? <div className="text-xs px-2">{it.plan_label}</div> : (
            <SearchableSelect testid={`presettle-hutang-${i}`} value={it.hutang_ref} onChange={(v) => upd(i, { hutang_ref: v })}
              options={it.hutang_ref && !hutangOptions.some((o) => o.value === it.hutang_ref) ? [{ value: it.hutang_ref, label: it.plan_label || "Hutang terpilih" }, ...hutangOptions] : hutangOptions}
              placeholder="— Pilih hutang / rencana DP —" emptyText="Tidak ada hutang"/>
          ))}
        </div>
        <div className="col-span-2"><Input type="date" data-testid={`presettle-date-${i}`} disabled={locked} value={it.transaction_date} onChange={(e) => upd(i, { transaction_date: e.target.value })}/></div>
        <div className="col-span-2"><NumberInput data-testid={`presettle-amount-${i}`} disabled={locked} value={it.amount} onChange={(e) => upd(i, { amount: e.target.value })} placeholder="Nominal"/></div>
        <div className="col-span-2"><Input data-testid={`presettle-desc-${i}`} disabled={locked} value={it.description} onChange={(e) => upd(i, { description: e.target.value })} placeholder="Deskripsi"/></div>
        <div className="col-span-1 flex items-center justify-end gap-0.5">
          {it.target === "awb" && !locked && (
            <button type="button" title={showDp ? "Batalkan DP (bayar full)" : "Bukan bayar full? Isi DP & sisa hutang vendor"} data-testid={`presettle-dp-toggle-${i}`}
              onClick={() => (showDp ? upd(i, { _dp: false, vendor_name: "", unpaid_amount: "" }) : upd(i, { _dp: true }))}
              className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${showDp ? "bg-amber-500 text-white border-amber-500" : "text-amber-700 border-amber-300 hover:bg-amber-50"}`}>DP</button>
          )}
          {locked ? <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${STATUS_STYLE.settled}`}>Settle</span> : <>
            <button type="button" title="Duplikat" onClick={() => dup(i)} className="p-1 rounded hover:bg-slate-100 text-slate-500"><Copy size={13}/></button>
            <button type="button" title="Hapus" data-testid={`presettle-del-${i}`} onClick={() => remove(i)} className="p-1 rounded hover:bg-red-50 text-red-600"><Trash2 size={13}/></button>
          </>}
        </div>
      </div>
      {showDp && (
        <div className="grid grid-cols-12 gap-2 items-center pl-2 border-l-2 border-amber-300">
          <div className="col-span-2 text-[10px] text-amber-800 font-bold uppercase leading-tight">Bayar DP<br/><span className="font-normal normal-case text-slate-500">isi sisa belum dibayar</span></div>
          <div className="col-span-4">
            {locked ? <div className="text-xs">{it.vendor_name || "-"}</div> : <VendorSelect testid={`presettle-vendor-${i}`} value={it.vendor_name} onChange={(v) => upd(i, { vendor_name: v })} vendors={vendors} onVendorsChange={loadVendors}/>}
          </div>
          <div className="col-span-3"><NumberInput data-testid={`presettle-unpaid-${i}`} disabled={locked} value={it.unpaid_amount} onChange={(e) => upd(i, { unpaid_amount: e.target.value })} placeholder="Sisa belum dibayar"/></div>
          <div className="col-span-3 text-[10px] text-slate-600">
            {parseFloat(it.unpaid_amount) > 0 ? <>Total biaya {fmtIDR((parseFloat(it.amount) || 0) + (parseFloat(it.unpaid_amount) || 0))}</> : "Sisa jadi hutang vendor"}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PresettleEditor({ items, setItems, orders, expenseCats, hutangOptions, amountLimit }) {
  const [vendors, setVendors] = useState([]);
  const loadVendors = () => api.get("/vendors").then((r) => setVendors(r.data || [])).catch(() => {});
  useEffect(() => { loadVendors(); }, []);
  const upd = (i, patch) => setItems(items.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const dup = (i) => setItems([...items.slice(0, i + 1), { ...items[i], _k: Math.random().toString(36).slice(2), item_id: null, status: "plan" }, ...items.slice(i + 1)]);
  const [focusKey, setFocusKey] = useState(null);
  const add = (k) => { const n = newItem(k); setItems([...items, n]); setFocusKey(n._k); };
  useEffect(() => {
    if (!focusKey) return;
    const el = document.querySelector(`[data-row-key="${focusKey}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => el.querySelector("input:not([disabled]), button[role=combobox], select")?.focus({ preventScroll: true }), 350);
    }
    setFocusKey(null);
  }, [focusKey]);
  const t = presettleTotals(items);
  const settled = items.filter((i) => i.status === "settled").reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const addButtons = (prefix) => Object.entries(TARGET_LABEL).map(([k, v]) => (
    <button key={k} type="button" data-testid={`${prefix}-${k}`} onClick={() => add(k)}
      className="text-[11px] font-bold text-blue-700 hover:text-white hover:bg-blue-600 border border-blue-200 hover:border-blue-600 bg-white rounded-full px-2.5 py-1 inline-flex items-center gap-1 transition-colors"><Plus size={12}/> {v}</button>
  ));
  return (
    <div data-testid="presettle-editor">
      <div className="sticky -top-6 z-20 -mx-3 px-3 pt-2 bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm mb-2" data-testid="presettle-toolbar">
        <div className="flex flex-wrap items-center justify-between gap-2 pb-2">
          <div className="flex items-center gap-3">
            <h4 className="text-[11px] uppercase tracking-[0.12em] font-bold text-slate-500">Rincian (presettle — belum dijurnal)</h4>
            <span className="text-[10px] text-slate-500" data-testid="presettle-toolbar-total">{items.length} item · <b className="font-mono text-slate-800">{fmtIDR(t.paid)}</b></span>
          </div>
          <div className="flex flex-wrap gap-1.5">{addButtons("presettle-add")}</div>
        </div>
        <div className="grid grid-cols-12 gap-2 px-2 pb-1 text-[9px] uppercase tracking-wide font-bold text-slate-500">
          <div className="col-span-2">Jenis</div><div className="col-span-3">Detail</div><div className="col-span-2">Tgl Transaksi</div><div className="col-span-2">Nominal dari panjar</div><div className="col-span-2">Deskripsi</div><div className="col-span-1"></div>
        </div>
      </div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={it._k} data-row-key={it._k}>
            <ItemRow it={it} i={i} upd={upd} remove={remove} dup={dup} orders={orders} expenseCats={expenseCats} vendors={vendors} loadVendors={loadVendors} hutangOptions={hutangOptions}/>
          </div>
        ))}
        {items.length === 0 && <div className="text-xs text-slate-400 py-3 text-center border border-dashed rounded">Belum ada item. Tambah lewat tombol di atas.</div>}
      </div>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" data-testid="presettle-summary">
        <div className="p-2 rounded bg-slate-50 border"><div className="text-[9px] uppercase font-bold text-slate-500">Dibayar dari panjar</div><b className="font-mono" data-testid="presettle-total-paid">{fmtIDR(t.paid)}</b></div>
        <div className="p-2 rounded bg-amber-50 border border-amber-200"><div className="text-[9px] uppercase font-bold text-amber-700">Akan jadi hutang vendor</div><b className="font-mono">{fmtIDR(t.unpaid)}</b></div>
        <div className="p-2 rounded bg-slate-50 border"><div className="text-[9px] uppercase font-bold text-slate-500">Total biaya</div><b className="font-mono">{fmtIDR(t.paid + t.unpaid)}</b></div>
        <div className="p-2 rounded bg-emerald-50 border border-emerald-200"><div className="text-[9px] uppercase font-bold text-emerald-700">Sudah settle</div><b className="font-mono">{fmtIDR(settled)}</b></div>
      </div>
      {amountLimit != null && t.paid > amountLimit + 0.5 && (
        <p className="text-xs text-rose-700 mt-2 font-semibold" data-testid="presettle-over-warning">
          Total rincian {fmtIDR(t.paid)} melebihi nilai panjar {fmtIDR(amountLimit)}. Ubah nilai panjar lewat Edit Panjar kalau memang bertambah.
        </p>
      )}
    </div>
  );
}
